import React, {useState} from 'react';
import {useApolloClient} from '@apollo/client';
import {Anchor, Button, Checkbox, Dialogue, Text, addToast} from '@albert-io/atomic';

import type {AdminSchool} from './useAdminSchools';
import {computeLockChange, computeUnlockChange} from './contentLockDerivation';
import type {GuideTree} from './contentLockDerivation';
import {AdminSchoolContentLocksDocument} from './adminContentLocks.queries.generated';
import {
  useLockContentMutation,
  useUnlockContentMutation
} from './contentLockMutations.queries.generated';
import {GUIDE_LEVEL_LOCK_TYPE} from './manageRestrictionsModal.constants';
import {SchoolRestrictionRow} from './SchoolRestrictionRow';

// Placeholder until the content-locking help-center article exists.
const LEARN_MORE_URL = '#';

interface RestrictionFormProps {
  guideLevelName: string;
  guideLevelId?: string;
  guideLevelTree?: GuideTree;
  schools: AdminSchool[];
  /** Ids of schools that already restrict this guide level — the initial toggle state. */
  initialRestrictedIds: string[];
  getLockedSourceIds: (schoolId: string) => Set<string>;
  getLockId: (schoolId: string, sourceId: string) => string | undefined;
  onClose: () => void;
}

export const RestrictionForm = ({
  guideLevelName,
  guideLevelId,
  guideLevelTree,
  schools,
  initialRestrictedIds,
  getLockedSourceIds,
  getLockId,
  onClose
}: RestrictionFormProps) => {
  const [restrictedSchoolIds, setRestrictedSchoolIds] = useState<Set<string>>(
    () => new Set(initialRestrictedIds)
  );
  // Baseline seed state, diffed against the edited set on confirm.
  const [initialIds] = useState<Set<string>>(() => new Set(initialRestrictedIds));
  const [acknowledged, setAcknowledged] = useState(false);
  const [saving, setSaving] = useState(false);

  const [lockContent] = useLockContentMutation();
  const [unlockContent] = useUnlockContentMutation();
  const client = useApolloClient();

  const toggleSchool = (schoolId: string) => {
    setRestrictedSchoolIds((prev) => {
      const next = new Set(prev);
      if (next.has(schoolId)) {
        next.delete(schoolId);
      } else {
        next.add(schoolId);
      }
      return next;
    });
  };

  const handleConfirm = async () => {
    const changedSchools = schools.filter(
      (school) => restrictedSchoolIds.has(school.id) !== initialIds.has(school.id)
    );

    if (!guideLevelId || changedSchools.length === 0) {
      onClose();
      return;
    }

    setSaving(true);

    try {
      await Promise.all(
        changedSchools.map(async (school) => {
          const lockedSourceIds = getLockedSourceIds(school.id);
          const wantsRestricted = restrictedSchoolIds.has(school.id);

          let change: {toLock: string[]; toUnlock: string[]};
          if (guideLevelTree) {
            change = wantsRestricted
              ? computeLockChange(guideLevelTree, guideLevelId, lockedSourceIds)
              : computeUnlockChange(guideLevelTree, guideLevelId, lockedSourceIds);
          } else {
            change = wantsRestricted
              ? {toLock: [guideLevelId], toUnlock: []}
              : {toLock: [], toUnlock: lockedSourceIds.has(guideLevelId) ? [guideLevelId] : []};
          }

          await Promise.all([
            ...change.toLock.map(async (sourceId) => {
              const {data} = await lockContent({
                variables: {
                  input: {lockType: GUIDE_LEVEL_LOCK_TYPE, sourceId, schoolId: school.id}
                }
              });
              // Ash mutations can resolve at the transport layer yet carry policy/validation errors.
              if (data?.lockContent?.errors?.length) {
                throw new Error(data.lockContent.errors[0]?.message ?? 'Failed to restrict content');
              }
            }),
            ...change.toUnlock.map(async (sourceId) => {
              const lockId = getLockId(school.id, sourceId);
              if (!lockId) {
                return;
              }
              const {data} = await unlockContent({variables: {id: lockId}});
              if (data?.unlockContent?.errors?.length) {
                throw new Error(
                  data.unlockContent.errors[0]?.message ?? 'Failed to unrestrict content'
                );
              }
            })
          ]);
        })
      );

      // Refresh each changed school's locks once (not once per mutation) so the useAdminContentLocks
      // watchers re-render the live indicators. A failed refresh must not fail an already-saved change.
      try {
        await Promise.all(
          changedSchools.map((school) =>
            client.query({
              query: AdminSchoolContentLocksDocument,
              variables: {schoolId: school.id},
              fetchPolicy: 'network-only'
            })
          )
        );
      } catch {
        // Best-effort refresh; the save itself succeeded.
      }

      addToast({
        color: 'positive',
        title: 'Restrictions updated',
        message: `Restrictions for ${guideLevelName} have been saved.`
      });
      onClose();
    } catch {
      addToast({
        color: 'negative',
        title: "Couldn't update restrictions",
        message: 'Something went wrong saving your changes. Please try again.'
      });
      setSaving(false);
    }
  };

  return (
    <>
      <Dialogue.Body>
        <Text as='div' color='secondary'>
          Restrict{' '}
          <Text bold>
            {guideLevelName}
          </Text>{' '}
          for each of your schools. Restricting blocks teachers and students at that school from
          seeing the question content outside of assignments you authorize.{' '}
          <Anchor href={LEARN_MORE_URL} underlined>
            Learn more
          </Anchor>
        </Text>

        <div className='mt-x4 max-h-[280px] overflow-y-auto rounded-x1 border border-border-primary'>
          <table className='w-full border-collapse'>
            <thead>
              <tr className='sticky top-0 z-10 background-primary'>
                <th className='w-full border-b border-border-primary px-x2 py-x1-half text-left'>
                  <Text bold size='s'>
                    School
                  </Text>
                </th>
                <th className='whitespace-nowrap border-b border-l border-border-primary px-x2 py-x1-half text-right'>
                  <Text bold size='s'>
                    Restrict
                  </Text>
                </th>
              </tr>
            </thead>
            <tbody>
              {schools.map((school) => (
                <SchoolRestrictionRow
                  key={school.id}
                  school={school}
                  guideLevelId={guideLevelId}
                  isRestricted={restrictedSchoolIds.has(school.id)}
                  onToggle={() => toggleSchool(school.id)}
                />
              ))}
            </tbody>
          </table>
        </div>

        <label className='mt-x4 flex cursor-pointer items-start gap-x1-half'>
          <Checkbox
            className='mt-x0-quarter shrink-0'
            checked={acknowledged}
            onChange={(event) => setAcknowledged(event.target.checked)}
            aria-hidden={false}
            label='I understand the impact of restricting this content'
            size='s'
          />
          <Text size='s' color='secondary'>
            This will restrict all questions in{' '}
            <Text size='s' bold>
              {guideLevelName}
            </Text>{' '}
            and any questions in levels beneath it for the selected schools.{' '}
            <Text size='s' bold>
              I understand the impact of restricting this content.
            </Text>
          </Text>
        </label>
      </Dialogue.Body>

      <Button
        className='mt-x4 w-full'
        color='primary'
        disabled={!acknowledged || saving}
        onClick={handleConfirm}
      >
        {saving ? 'Saving…' : 'Confirm'}
      </Button>
    </>
  );
};

export default RestrictionForm;
